/**
 * An in-app replacement for window.confirm().
 *
 * The panel ships as an installable PWA (manifest display: "standalone"), and a
 * native confirm() is not dependable there: iOS has long rendered it
 * unreliably from a home-screen app, and in Safari an admin who ticks "Don't
 * show more alerts on this page" makes confirm() return false for the rest of
 * the session. Either way the caller sees a plain `false` and silently does
 * nothing, which reads to the admin as a dead button.
 *
 * Usage mirrors what it replaces, so a call site changes by one line:
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm({ message: 'Delete this limit?' }))) return;
 */

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const ConfirmContext = createContext(null);

const TONES = {
  danger: {
    icon: 'text-red-600 dark:text-red-400',
    iconBg: 'bg-red-100 dark:bg-red-900/40',
    button: 'bg-red-600 hover:bg-red-700 active:bg-red-800'
  },
  warning: {
    icon: 'text-amber-600 dark:text-amber-400',
    iconBg: 'bg-amber-100 dark:bg-amber-900/40',
    button: 'bg-amber-600 hover:bg-amber-700 active:bg-amber-800'
  },
  primary: {
    icon: 'text-blue-600 dark:text-blue-400',
    iconBg: 'bg-blue-100 dark:bg-blue-900/40',
    button: 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
  }
};

export const ConfirmProvider = ({ children }) => {
  const [request, setRequest] = useState(null);
  // The promise's resolve lives in a ref so re-renders never lose the caller.
  const resolveRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    const opts = typeof options === 'string' ? { message: options } : options;
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setRequest({
        title: opts.title || null,
        message: opts.message || '',
        confirmLabel: opts.confirmLabel || 'Confirm',
        cancelLabel: opts.cancelLabel || 'Cancel',
        tone: TONES[opts.tone] ? opts.tone : 'danger'
      });
    });
  }, []);

  const settle = useCallback((answer) => {
    setRequest(null);
    const resolve = resolveRef.current;
    resolveRef.current = null;
    if (resolve) resolve(answer);
  }, []);

  // Escape cancels, Enter confirms, and the body cannot scroll behind the sheet
  useEffect(() => {
    if (!request) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') settle(false);
      if (e.key === 'Enter') settle(true);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [request, settle]);

  // A caller left waiting on unmount would hang forever, so answer no.
  useEffect(() => () => {
    if (resolveRef.current) resolveRef.current(false);
  }, []);

  const tone = request ? TONES[request.tone] : TONES.danger;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {request && (
        <div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => settle(false)}
        >
          <div
            className="w-full sm:max-w-md bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-xl overflow-hidden"
            // Tapping the sheet itself must not read as tapping the backdrop
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 sm:p-6">
              <div className="flex gap-4">
                <div className={`shrink-0 w-11 h-11 rounded-full flex items-center justify-center ${tone.iconBg}`}>
                  <ExclamationTriangleIcon className={`w-6 h-6 ${tone.icon}`} />
                </div>
                <div className="flex-1 min-w-0 pt-0.5">
                  {request.title && (
                    <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">
                      {request.title}
                    </h3>
                  )}
                  <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-line break-words">
                    {request.message}
                  </p>
                </div>
              </div>
            </div>

            {/* Full-width targets, and padding for the iOS home indicator */}
            <div
              className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 px-5 sm:px-6 pb-5 sm:pb-6"
              style={{ paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom))' }}
            >
              <button
                type="button"
                onClick={() => settle(false)}
                className="px-4 py-3 sm:py-2.5 rounded-lg font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors touch-manipulation"
              >
                {request.cancelLabel}
              </button>
              <button
                type="button"
                autoFocus
                onClick={() => settle(true)}
                className={`px-4 py-3 sm:py-2.5 rounded-lg font-semibold text-white transition-colors touch-manipulation ${tone.button}`}
              >
                {request.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
};

/**
 * @returns {(options: string|{title?, message, confirmLabel?, cancelLabel?, tone?}) => Promise<boolean>}
 */
export const useConfirm = () => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return context;
};

export default ConfirmProvider;
